/** Subaccounts seen trading this session, so the agent stops showing trial copy before the server's tier read catches up. */

import { NotificationType, type UserNotification } from "@decibeltrade/sdk";
import { create } from "zustand";

import { addressComparisonKey } from "@/utils/address";
import { getFilledSize } from "@/utils/order-event-sizes";

const TERMINAL_TYPES: ReadonlySet<NotificationType> = new Set([
  NotificationType.OrderCancelled,
  NotificationType.OrderRejected,
  NotificationType.OrderErrored,
]);

export function isFillNotification(notification: UserNotification["notification"]): boolean {
  const type = notification.notification_type;
  if (type === NotificationType.OrderFilled || type === NotificationType.OrderPartiallyFilled) {
    return true;
  }
  return TERMINAL_TYPES.has(type) && (getFilledSize(type, notification.order) ?? 0) > 0;
}

interface AgentObservedFillsState {
  /** First fill seen per subaccount comparison key, in client epoch ms. */
  observedAt: Map<string, number>;
  record: (subaccountAddress: string, notification: UserNotification["notification"]) => void;
}

export const useAgentObservedFills = create<AgentObservedFillsState>((set) => ({
  observedAt: new Map(),
  record: (subaccountAddress, notification) => {
    if (!isFillNotification(notification)) return;
    const key = addressComparisonKey(subaccountAddress);
    set((state) => {
      if (state.observedAt.has(key)) return state;
      const observedAt = new Map(state.observedAt);
      observedAt.set(key, Date.now());
      return { observedAt };
    });
  },
}));

export function selectObservedFillAt(
  state: AgentObservedFillsState,
  subaccountAddress: string,
): number | undefined {
  return state.observedAt.get(addressComparisonKey(subaccountAddress));
}

export function useObservedFillAt(subaccountAddress: string | undefined): number | undefined {
  return useAgentObservedFills((state) =>
    subaccountAddress === undefined ? undefined : selectObservedFillAt(state, subaccountAddress),
  );
}
